import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { AuthPage } from "../components/AuthPage";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { useAuth, type SignInResult } from "../lib/auth";
import { credentialFailure } from "../lib/credentials";

import styles from "./login.module.css";

interface LoginLocationState {
  from?: { pathname?: string; search?: string };
  /** Set when a session had to be signed out to meet its code. */
  signInAgain?: boolean;
  email?: string;
}

function authErrorMessage(err: unknown) {
  switch (credentialFailure(err)) {
    case "wrong_password":
      return "Email or password is incorrect.";
    case "wrong_code":
      return "That code did not match. Enter the one your app shows now.";
    case "too_many_attempts":
      return "Too many attempts. Try again later.";
    default:
      return "Login failed. Try again.";
  }
}

/**
 * One sign-in, for every Company.
 *
 * A Company on its own Current RMS signed in by authorizing that account
 * until ADR-0019 and ADR-0020 were superseded: the authorize step
 * refuses an application registered in somebody else's account, so the
 * one Custom Application the whole design rested on was never going to
 * serve a second partner. What is left is the account intake issues
 * itself, which is what every other Company already used.
 *
 * An account with an authenticator app finishes on a second step, the
 * code, on this same page.
 */
export default function LoginPage() {
  const { configured, loading, signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LoginLocationState | null;
  const redirectTo = `${state?.from?.pathname || "/"}${state?.from?.search || ""}`;
  const [email, setEmail] = useState(state?.email ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<Extract<
    SignInResult,
    { status: "needs_code" }
  > | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, navigate, redirectTo, user]);

  if (!loading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result?.status === "needs_code") {
        setPending(result);
        return;
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!pending) return;
    setError(null);
    setSubmitting(true);
    try {
      await pending.verify(code.replace(/\s/g, ""));
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (pending) {
    return (
      <AuthPage
        title="Enter your code"
        description="Open your authenticator app and enter the 6-digit code for HireOS."
      >
        <form onSubmit={onVerify} className={styles.stack}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Code</span>
            <input
              autoComplete="one-time-code"
              autoFocus
              className={styles.input}
              disabled={submitting}
              inputMode="numeric"
              maxLength={8}
              onChange={(event) => setCode(event.target.value)}
              required
              value={code}
            />
          </label>

          {error ? <Banner variant="error">{error}</Banner> : null}

          <div className={styles.actions}>
            <Button
              className={styles.submit}
              disabled={submitting}
              loading={submitting}
              type="submit"
            >
              Verify
            </Button>
          </div>
          <p className={styles.footNote}>
            <Button
              size="sm"
              type="button"
              variant="link"
              onClick={() => {
                setPending(null);
                setCode("");
                setPassword("");
                setError(null);
              }}
            >
              Use a different account
            </Button>
          </p>
        </form>
      </AuthPage>
    );
  }

  return (
    <AuthPage title="Login">
      {!configured ? (
        <Banner variant="error">
          Firebase Auth is not configured for this build.
        </Banner>
      ) : (
        <form onSubmit={onSubmit} className={styles.stack}>
          {state?.signInAgain ? (
            <Banner variant="info">
              Log in again with the code from your authenticator app.
            </Banner>
          ) : null}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Email</span>
            <input
              autoComplete="email"
              className={styles.input}
              disabled={submitting}
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Password</span>
            <input
              autoComplete="current-password"
              className={styles.input}
              disabled={submitting}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>

          {error ? <Banner variant="error">{error}</Banner> : null}

          <div className={styles.actions}>
            <Button
              className={styles.submit}
              disabled={submitting}
              loading={submitting}
              type="submit"
            >
              Login
            </Button>
          </div>
          <p className={styles.footNote}>
            <Link className={styles.footLink} to="/forgot-password">
              Forgot password?
            </Link>
          </p>
        </form>
      )}
    </AuthPage>
  );
}
